// Localização: ./src/pages/Ponto/DashboardPonto.js (Versão Final - Foco Ponto)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    getPontoStatus, 
    getAgendamentosPendentes, 
    registerCheckIn, 
    registerCheckOut 
} from '../../api/api'; 
import { useAuth } from '../../context/AuthContext';
import { useGeolocation } from '../../hooks/useGeolocation';
import { useNotification } from '../../context/NotificationContext';
import LocationPermissionModal from '../../components/Ponto/LocationPermissionModal';

import { 
    Clock, 
    CheckCircle, 
    XCircle, 
    MapPin, 
    Clipboard, 
    Loader2, 
    AlertCircle,
    Calendar
} from 'lucide-react'; 

// Função auxiliar para formatar a data/hora para o card
const formatarHora = (dataString) => {
    if (!dataString) return '--:--';
    const d = new Date(dataString);
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatarPeriodo = (inicio, fim) => {
    return `${formatarHora(inicio)} - ${formatarHora(fim)}`;
};

const DashboardPonto = () => {
    const { } = useAuth();
    const navigate = useNavigate();
    const { success } = useNotification();
    
    // Hook de geolocalização
    const { captureLocation, loading: geolocationLoading } = useGeolocation();
    
    // ESTADOS DE PONTO (Mantidos)
    const [proximoAgendamento, setProximoAgendamento] = useState(null);
    const [atendimentoAtivo, setAtendimentoAtivo] = useState(null);
    const [proximosCheckIns, setProximosCheckIns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estados para modais
    const [showLocationPermissionModal, setShowLocationPermissionModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null); // 'checkin' ou 'checkout'
    const [pendingCoordinates, setPendingCoordinates] = useState(null);


    // -----------------------------------------------------------
    // 1. CARREGAR DADOS INICIAIS (Status de Ponto + Próximo Agendamento)
    // -----------------------------------------------------------
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        
        try {
            // A. Buscar Status de Check-in Ativo (/api/ponto/status)
            const statusResponse = await getPontoStatus(); 
            
            if (statusResponse.data.is_checked_in) {
                // Se ESTÁ em atendimento, armazena os dados do atendimento ativo
                setAtendimentoAtivo({ 
                    ...statusResponse.data.data,
                    is_active: true,
                });
                setProximoAgendamento(null);
                
                // Buscar próximos check-ins (excluindo o atual)
                const agendamentosResponse = await getAgendamentosPendentes({ limit: 10 });
                
                // Verificar se a resposta é um array ou está dentro de data
                const agendamentos = Array.isArray(agendamentosResponse.data) 
                    ? agendamentosResponse.data 
                    : (agendamentosResponse.data?.data || []);
                
                console.log('Agendamentos recebidos (com atendimento ativo):', agendamentos);
                
                if (agendamentos.length > 0) {
                    // Filtrar o agendamento ativo e pegar os próximos
                    // O backend retorna agendamento_id no statusResponse.data.data
                    const agendamentoAtivoId = statusResponse.data.data?.agendamento_id || statusResponse.data.data?.id;
                    console.log('Agendamento ativo ID:', agendamentoAtivoId);
                    console.log('Todos os agendamentos antes do filtro:', agendamentos);
                    
                    const proximos = agendamentos.filter(
                        ag => {
                            const agId = ag.id || ag.agendamento_id;
                            return agId !== agendamentoAtivoId;
                        }
                    );
                    console.log('Próximos check-ins (filtrados):', proximos);
                    setProximosCheckIns(proximos);
                } else {
                    setProximosCheckIns([]);
                }
            } else {
                setAtendimentoAtivo(null);
                
                // B. Se NÃO está em atendimento, busca todos os agendamentos pendentes
                const agendamentosResponse = await getAgendamentosPendentes({ limit: 10 }); 
                
                // Verificar se a resposta é um array ou está dentro de data
                const agendamentos = Array.isArray(agendamentosResponse.data) 
                    ? agendamentosResponse.data 
                    : (agendamentosResponse.data?.data || []);
                
                console.log('Agendamentos recebidos:', agendamentos);
                
                if (agendamentos.length > 0) {
                    setProximoAgendamento(agendamentos[0]);
                    // Os próximos são todos exceto o primeiro
                    const proximos = agendamentos.slice(1);
                    console.log('Próximos check-ins:', proximos);
                    setProximosCheckIns(proximos);
                } else {
                    setProximoAgendamento(null);
                    setProximosCheckIns([]);
                }
            }
        } catch (err) {
            console.error('Erro ao buscar dados do Dashboard:', err.response?.data || err);
            const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Falha ao carregar o painel. Tente novamente.';
            
            if (err.response?.status === 404 && !err.response?.data?.message) {
                 setError(''); 
                 setAtendimentoAtivo(null);
                 setProximoAgendamento(null);
                 setProximosCheckIns([]);
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // -----------------------------------------------------------
    // 2. HANDLERS (Check-in, Check-out)
    // -----------------------------------------------------------
    
    // Handler inicial - mostra modal de permissão
    const handleCheckInClick = () => {
        if (atendimentoAtivo || !proximoAgendamento) return;
        setPendingAction('checkin');
        setShowLocationPermissionModal(true);
    };

    // Handler quando usuário confirma permissão de localização
    const handleConfirmLocationPermission = async () => {
        setShowLocationPermissionModal(false);
        
        // Tentar capturar localização
        let latitude = null;
        let longitude = null;
        
        try {
            const coords = await captureLocation();
            if (coords) {
                latitude = coords.latitude;
                longitude = coords.longitude;
            }
        } catch (err) {
            // Log do erro, mas continua sem localização
            console.warn('Não foi possível obter localização:', err.message);
        }
        
        setPendingCoordinates({ latitude, longitude });
        
        // Executar diretamente sem modal de confirmação adicional
        if (pendingAction === 'checkin') {
            await handleCheckIn();
        } else if (pendingAction === 'checkout') {
            await handleCheckOut();
        }
    };

    // Handler quando usuário escolhe continuar sem localização
    const handleSkipLocation = async () => {
        setShowLocationPermissionModal(false);
        setPendingCoordinates({ latitude: null, longitude: null });
        
        // Executar diretamente sem modal de confirmação adicional
        if (pendingAction === 'checkin') {
            await handleCheckIn();
        } else if (pendingAction === 'checkout') {
            await handleCheckOut();
        }
    };

    // Handler final de check-in (executado diretamente após localização)
    const handleCheckIn = async () => {
        if (atendimentoAtivo || !proximoAgendamento) return;

        setLoading(true);
        setError('');
        
        const payload = { 
            agendamento_id: proximoAgendamento.id,
            latitude: pendingCoordinates?.latitude || null,
            longitude: pendingCoordinates?.longitude || null
        };

        try {
            await registerCheckIn(payload); 
            fetchData();
            // Limpar estados
            setPendingAction(null);
            setPendingCoordinates(null);
            // Mostrar notificação de sucesso
            const pacienteNome = proximoAgendamento?.nome_paciente || proximoAgendamento?.paciente?.nome_completo || 'o paciente';
            success(`Check-in realizado com sucesso! Você pode acessar o Diário de Cuidados para ${pacienteNome}.`, 6000);
        } catch (err) {
            console.error('Erro no Check-in:', err.response || err);
            setError(err.response?.data?.detail || 'Erro ao registrar Check-in. Verifique se o agendamento está pendente e tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    // Handler inicial - mostra modal de permissão
    const handleCheckOutClick = () => {
        if (!atendimentoAtivo) return;
        setPendingAction('checkout');
        setShowLocationPermissionModal(true);
    };

    // Handler final de check-out (executado diretamente após localização)
    const handleCheckOut = async () => {
        if (!atendimentoAtivo) return;

        setLoading(true);
        setError('');
        
        const payload = { 
            agendamento_id: atendimentoAtivo.agendamento_id,
            latitude: pendingCoordinates?.latitude || null,
            longitude: pendingCoordinates?.longitude || null
        };

        try {
            await registerCheckOut(payload); 
            fetchData();
            // Limpar estados
            setPendingAction(null);
            setPendingCoordinates(null);
            // Mostrar notificação de sucesso
            const pacienteNome = atendimentoAtivo?.nome_paciente || atendimentoAtivo?.paciente?.nome_completo || 'o paciente';
            success(`Check-out realizado com sucesso! O atendimento de ${pacienteNome} foi concluído e registrado.`, 6000);
        } catch (err) {
            console.error('Erro no Check-out:', err.response || err);
            setError(err.response?.data?.detail || 'Erro ao registrar Check-out. Verifique o status do agendamento.');
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------------------------
    // 3. RENDERIZAÇÃO DE COMPONENTES
    // -----------------------------------------------------------

    const RenderAtendimentoCard = () => {
        const item = atendimentoAtivo || proximoAgendamento;

        if (loading) {
            return (
                <div className="flex justify-center items-center h-48 bg-white rounded-xl shadow-md mb-6 border border-gray-200">
                    <Loader2 className="animate-spin text-teal-600 mr-2" size={24} />
                    <span className="text-gray-600">Carregando status...</span>
                </div>
            );
        }

        if (!item) {
            return (
                <div className="text-center p-8 bg-white rounded-xl shadow-md mb-6 border border-gray-200">
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
                    <h3 className="text-xl font-semibold text-gray-800">Dia de folga ou Agendamentos concluídos!</h3>
                    <p className="text-gray-600 mt-2">Você está livre de atendimentos no momento.</p>
                </div>
            );
        }

        const isActive = !!atendimentoAtivo;
        const pacienteName = item.nome_paciente || item.paciente?.nome_completo;
        const endereco = item.endereco || item.paciente?.endereco;

        return (
            <div className={`p-6 rounded-xl shadow-md mb-6 border transition-all duration-300 ${isActive ? 'bg-teal-50 border-teal-200 ring-1 ring-teal-100/50' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-3">
                    <h2 className={`text-2xl font-bold ${isActive ? 'text-teal-800' : 'text-gray-800'}`}>
                        {isActive ? 'Atendimento Atual' : 'Próximo Atendimento'}
                    </h2>
                    <span className={`text-sm font-semibold px-3 py-1 rounded-full ${isActive ? 'bg-teal-600 text-white' : 'bg-yellow-100 text-yellow-800'}`}>
                        {isActive ? 'Em Andamento' : 'Pendente'}
                    </span>
                </div>

                <h3 className="text-xl font-semibold text-gray-900">{pacienteName}</h3>
                <p className="text-gray-600 flex items-center mt-1">
                    <MapPin size={16} className="mr-2 flex-shrink-0" /> {endereco}
                </p>
                <div className="mt-3 text-sm grid grid-cols-2 gap-2">
                    <p className="text-gray-700">
                        <Clock size={16} className="inline mr-1" /> **Horário:** {formatarPeriodo(item.data_hora_inicio, item.data_hora_fim)}
                    </p>
                    <p className="text-gray-700">
                        <Clipboard size={16} className="inline mr-1" /> **Cuidado:** {item.tipo_cuidado}
                    </p>
                    {isActive && (
                          <p className="col-span-2 text-sm text-green-600 font-medium mt-1">
                             <CheckCircle size={16} className="inline mr-1" /> Check-in realizado às: {formatarHora(item.check_in_time)}
                          </p>
                    )}
                </div>

                <div className="mt-4 text-sm text-right">
                    <button 
                        onClick={() => navigate('/diario')} 
                        className="text-teal-600 hover:text-teal-800 font-medium flex items-center justify-end w-full"
                    >
                        <Clipboard size={16} className="mr-1"/> Acessar Diário de Cuidados
                    </button>
                </div>
            </div>
        );
    };

    const RenderBotoesPonto = () => {
        const checkInDisabled = atendimentoAtivo || !proximoAgendamento || loading;
        const checkOutDisabled = !atendimentoAtivo || loading;

        return (
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex gap-4">
                    <button
                        onClick={handleCheckInClick}
                        disabled={checkInDisabled}
                        className={`flex-1 flex items-center justify-center px-4 py-3 rounded-xl font-semibold transition-all duration-200 
                            ${checkInDisabled
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300'
                                : 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg border border-green-700'
                            }`}
                    >
                        <CheckCircle size={20} className="mr-2" />
                        {loading ? 'Carregando...' : 'Check-in (Iniciar)'}
                    </button>
                    <button
                        onClick={handleCheckOutClick}
                        disabled={checkOutDisabled}
                        className={`flex-1 flex items-center justify-center px-4 py-3 rounded-xl font-semibold transition-all duration-200 
                            ${checkOutDisabled
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed border border-gray-300'
                                : 'bg-red-600 text-white hover:bg-red-700 shadow-md hover:shadow-lg border border-red-700'
                            }`}
                    >
                        <XCircle size={20} className="mr-2" />
                        {loading ? 'Carregando...' : 'Check-out (Finalizar)'}
                    </button>
                </div>
                {geolocationLoading && (
                    <div className="text-xs text-gray-500 mt-1 flex items-center justify-center">
                        <Loader2 size={12} className="mr-1 animate-spin" />
                        Obtendo localização...
                    </div>
                )}
            </div>
        );
    };

    // Função auxiliar para formatar data completa
    const formatarDataCompleta = (dataString) => {
        if (!dataString) return '';
        const d = new Date(dataString);
        const hoje = new Date();
        const amanha = new Date(hoje);
        amanha.setDate(amanha.getDate() + 1);
        
        const dataAgendamento = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const dataHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
        const dataAmanha = new Date(amanha.getFullYear(), amanha.getMonth(), amanha.getDate());
        
        if (dataAgendamento.getTime() === dataHoje.getTime()) {
            return 'Hoje';
        } else if (dataAgendamento.getTime() === dataAmanha.getTime()) {
            return 'Amanhã';
        } else {
            return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
        }
    };

    const RenderListaProximosCheckIns = () => {
        if (loading) {
            return null;
        }

        // Verificar se há próximos check-ins para exibir
        if (!proximosCheckIns || !Array.isArray(proximosCheckIns) || proximosCheckIns.length === 0) {
            // Debug: mostrar mensagem quando não há próximos check-ins
            console.log('RenderListaProximosCheckIns: Não há próximos check-ins para exibir', {
                proximosCheckIns,
                isArray: Array.isArray(proximosCheckIns),
                length: proximosCheckIns?.length
            });
            return null;
        }

        console.log('RenderListaProximosCheckIns: Renderizando lista com', proximosCheckIns.length, 'itens');

        return (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-6">
                <div className="p-4 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800 flex items-center">
                        <Calendar size={20} className="mr-2 text-teal-600" />
                        Próximos Check-ins
                    </h2>
                </div>
                <div className="divide-y divide-gray-100">
                    {proximosCheckIns.map((agendamento, index) => {
                        const pacienteName = agendamento.nome_paciente || agendamento.paciente?.nome_completo || 'Paciente';
                        const endereco = agendamento.endereco || agendamento.paciente?.endereco || 'Endereço não informado';
                        const dataFormatada = formatarDataCompleta(agendamento.data_hora_inicio);
                        
                        return (
                            <div 
                                key={agendamento.id || index} 
                                className="p-4 hover:bg-gray-50 transition-colors duration-150"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-semibold text-gray-900">{pacienteName}</h3>
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                {dataFormatada}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 flex items-center mt-1">
                                            <MapPin size={14} className="mr-1.5 flex-shrink-0" />
                                            {endereco}
                                        </p>
                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                            <span className="flex items-center">
                                                <Clock size={14} className="mr-1" />
                                                {formatarPeriodo(agendamento.data_hora_inicio, agendamento.data_hora_fim)}
                                            </span>
                                            <span className="flex items-center">
                                                <Clipboard size={14} className="mr-1" />
                                                {agendamento.tipo_cuidado}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };


    return (
        // 💡 Ajuste de layout: Usamos 'min-h-screen' para cobrir toda a altura
        // e 'p-4' para padding em mobile, que é suficiente.
        <div className="min-h-screen p-4 sm:p-6 lg:p-8"> 
            {error && <div className="mb-4 p-3 text-red-700 bg-red-50 rounded-lg border border-red-200 flex items-center justify-center shadow-sm">
                <AlertCircle size={20} className="mr-2"/> {error}
            </div>}

            <div className="max-w-xl mx-auto">
                <RenderAtendimentoCard />
                <RenderBotoesPonto />
                <RenderListaProximosCheckIns />
            </div>

            {/* Modal de Permissão de Localização */}
            <LocationPermissionModal
                isOpen={showLocationPermissionModal}
                onClose={() => {
                    setShowLocationPermissionModal(false);
                    setPendingAction(null);
                    setPendingCoordinates(null);
                }}
                onConfirm={handleConfirmLocationPermission}
                onSkip={handleSkipLocation}
                actionType={pendingAction || 'checkin'}
            />

            {/* 🛑 Simulação da barra de navegação inferior REMOVIDA */}
        </div>
    );
};

export default DashboardPonto;